import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { APP_META } from '@/config/app';
import { useSession } from '@/features/auth/hooks/use-session';
import { useTablasTarjetas } from '@/lib/tabla-tarjetas';
import { cn } from '@/lib/utils';

import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { useMenuLateral } from './use-menu-lateral';

/**
 * Estructura principal del panel autenticado.
 *
 * - Mantiene sincronizada la sesión con el backend (`useSession`).
 * - Gestiona el cajón de navegación en móvil. El cierre al navegar lo dispara
 *   el propio enlace del menú (`onClose` en `Sidebar`), por lo que no se
 *   necesita un efecto que observe la ruta.
 * - Activa el formato de **tarjetas** de las tablas anchas en móvil
 *   (`useTablasTarjetas`): se llama aquí porque el armazón se dibuja una sola vez
 *   y cubre también los diálogos.
 * - Gestiona el **menú contraído** de escritorio (`useMenuLateral`), que recuerda
 *   la preferencia entre visitas.
 */
export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { contraido, alternar } = useMenuLateral();

  // Refresca permisos/datos del usuario desde el backend de forma periódica.
  useSession();

  // En el celular las tablas de cinco columnas o más se leen como tarjetas.
  useTablasTarjetas();

  return (
    <div className="min-h-dvh bg-slate-50">
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        contraido={contraido}
      />

      <div
        className={cn(
          'flex min-h-dvh min-w-0 flex-col transition-[padding] duration-300 ease-out',
          contraido ? 'lg:pl-[4.75rem]' : 'lg:pl-[17.5rem]',
        )}
      >
        <Topbar
          onOpenSidebar={() => setMobileOpen(true)}
          contraido={contraido}
          onToggleSidebar={alternar}
        />

        <main className="mx-auto w-full max-w-[100rem] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>

        <footer className="border-t border-ink-100 px-4 py-4 sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-[100rem] flex-col gap-1 text-xs text-ink-400 sm:flex-row sm:items-center sm:justify-between">
            <span>
              © {new Date().getFullYear()} {APP_META.vendor} · {APP_META.name} v{APP_META.version}
            </span>
            <span className="font-mono">Sesión protegida con JWT</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
